/**
 * SectionHeader — shared aspect. Eyebrow label + optional amber
 * count + optional right-aligned subtitle. Two surface tones:
 *   light — on cream / linen backgrounds, uses theme.textOnSurfaceDim
 *   dark  — on forest / dark backgrounds, uses linen alpha
 *
 * Used wherever a row of cards or list needs a header bar.
 */
import { Text, View } from "react-native";

import type { PaneTheme } from "../brand/paneThemes";

export type SectionHeaderTone = "light" | "dark";

export type SectionHeaderProps = {
  theme: PaneTheme;
  title: string;
  /** Optional amber count rendered next to the title. */
  count?: number;
  /** Optional right-aligned subtitle. */
  subtitle?: string;
  /** "light" (default) or "dark" surface context. */
  tone?: SectionHeaderTone;
};

export function SectionHeader({
  theme,
  title,
  count,
  subtitle,
  tone = "light",
}: SectionHeaderProps) {
  const labelColor =
    tone === "dark" ? "rgba(247,244,238,0.62)" : theme.textOnSurfaceDim;
  const subtitleColor =
    tone === "dark" ? "rgba(247,244,238,0.55)" : theme.textOnSurfaceDim;
  return (
    <View
      style={{
        marginBottom: 14,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <Text
          style={{
            fontFamily: theme.bodyBoldFont,
            fontSize: 10.5,
            letterSpacing: 2.1,
            textTransform: "uppercase",
            color: labelColor,
          }}
        >
          {title}
        </Text>
        {count != null && count > 0 ? (
          <Text
            style={{
              fontFamily: theme.displayMediumFont,
              fontSize: 14,
              color: theme.accent,
              letterSpacing: 0,
            }}
          >
            {count}
          </Text>
        ) : null}
      </View>
      {subtitle ? (
        <Text
          style={{
            fontFamily: theme.bodyFont,
            fontSize: 11.5,
            color: subtitleColor,
          }}
        >
          {subtitle}
        </Text>
      ) : null}
    </View>
  );
}
